import { useAuth } from '@clerk/clerk-react';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { z } from 'zod';
import { workflowsApi } from '~/api/def/servers/defaultServer';
import {
  AgentEvent,
  TaskName,
  TaskState,
  Workflow,
  WorkflowStateResponse,
} from '~/atoms/schemas/workflowSchemas';
import { getOrCreateWorkflowAtom } from '~/atoms/singleWorkflowAtom';

import {
  SSEConnection,
  SSEConnectionConfig,
  createSSEConnection,
  subscribe,
} from './sseConnection';

export type { AgentEvent, Workflow };

export const useSingleWorkflow = (workflowId: string) => {
  const workflowAtom = getOrCreateWorkflowAtom(workflowId);
  const { getToken } = useAuth();
  const [workflowState, setWorkflowState] = useAtom(workflowAtom);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const seenEventKeysRef = useRef<Set<string>>(new Set());

  // Helper function to create a unique key for an event record
  const createEventKey = useCallback((data: WorkflowStateResponse): string => {
    // Use JSON string directly as the unique key - simple and reliable
    return JSON.stringify(data);
  }, []);

  // Handler for processing different event types
  const handleSSEData = useCallback(
    (data: WorkflowStateResponse) => {
      // Validate data structure
      if (
        !data ||
        typeof data !== 'object' ||
        !('type' in data) ||
        !('content' in data)
      ) {
        console.error('Invalid SSE data format:', data);
        return;
      }

      try {
        // Process based on type
        if (
          data.type === 'workflow_state' &&
          data.content &&
          'task_states' in data.content
        ) {
          // It's a workflow update
          const workflow = data.content as Workflow;
          setWorkflowState((prev) => ({ ...prev, workflow }));
        } else if (
          data.type === 'llm_event' &&
          data.content &&
          'event_name' in data.content
        ) {
          // It's an agent event
          const agentEvent = data.content as AgentEvent;
          const eventKey = createEventKey(data);

          setWorkflowState((prev) => {
            // Check if we've already seen this exact event
            if (seenEventKeysRef.current.has(eventKey)) {
              // Already seen this event, just update current
              return {
                ...prev,
                currentAgentEvent: agentEvent,
              };
            }

            // Add to seen events set and agent events array
            seenEventKeysRef.current.add(eventKey);
            return {
              ...prev,
              currentAgentEvent: agentEvent,
              agentEvents: [...prev.agentEvents, agentEvent],
            };
          });
        } else {
          console.warn('Unknown event type or invalid content:', data);
        }
      } catch (error) {
        console.error('Error processing SSE event:', error);
      }
    },
    [setWorkflowState, createEventKey],
  );

  useEffect(() => {
    // Create SSE connection for this workflow
    const workflowConnection: SSEConnection<
      WorkflowStateResponse,
      WorkflowStateResponse
    > = createSSEConnection<WorkflowStateResponse, WorkflowStateResponse>();

    const config: SSEConnectionConfig<
      WorkflowStateResponse,
      WorkflowStateResponse
    > = {
      url: `${workflowsApi.url}/workflows/${workflowId}`,
      schema: z.any(), // Accept any data and validate in the handler
      dataConverter: (data) => data,
    };

    // Subscribe to workflow updates
    unsubscribeRef.current = subscribe<
      WorkflowStateResponse,
      WorkflowStateResponse
    >(
      workflowConnection,
      config,
      handleSSEData,
      (error) => {
        setWorkflowState((prev) => ({ ...prev, error }));
      },
      (loading) => {
        setWorkflowState((prev) => ({ ...prev, isLoading: loading }));
      },
      getToken,
      `SingleWorkflow-${workflowId}`,
    );

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [getToken, workflowId, setWorkflowState, handleSSEData]);

  // Memoized workflow to prevent unnecessary re-renders
  const memoizedWorkflow = useMemo(
    () => workflowState.workflow,
    [workflowState.workflow],
  );

  // Memoized current agent event to prevent unnecessary re-renders
  const memoizedAgentEvent = useMemo(
    () => workflowState.currentAgentEvent,
    [workflowState.currentAgentEvent],
  );

  // Memoized agent events array to prevent unnecessary re-renders
  const memoizedAgentEvents = useMemo(
    () => workflowState.agentEvents,
    [workflowState.agentEvents],
  );

  // Derived and memoized isActive state
  const isActive = useMemo(() => {
    return (
      memoizedWorkflow?.status === 'running' ||
      memoizedWorkflow?.status === 'starting'
    );
  }, [memoizedWorkflow]);

  // Memoized loading and error states
  const isLoading = useMemo(
    () => workflowState.isLoading,
    [workflowState.isLoading],
  );
  const error = useMemo(() => workflowState.error, [workflowState.error]);

  // Return a memoized object to prevent unnecessary re-renders when passed as props
  return useMemo(
    () => ({
      workflow: memoizedWorkflow,
      isLoading,
      error,
      isActive,
      currentAgentEvent: memoizedAgentEvent,
      agentEvents: memoizedAgentEvents,
    }),
    [
      memoizedWorkflow,
      isLoading,
      error,
      isActive,
      memoizedAgentEvent,
      memoizedAgentEvents,
    ],
  );
};

export type TaskEntry = {
  taskName: TaskName;
  taskState: TaskState;
  workflowId: string;
  startedAt: string;
};

export const getTasksFromWorkflow = (workflow: Workflow): TaskEntry[] => {
  if (!workflow) return [];

  // Get task states from either task_states or tasks property
  const taskStates = workflow.task_states || workflow.tasks || {};

  return Object.keys(taskStates).map((taskName) => {
    const taskState = taskStates[
      taskName as keyof typeof taskStates
    ] as TaskState;
    return {
      taskName: taskName as TaskName,
      taskState,
      workflowId: workflow.workflow_id,
      startedAt: taskState.created_at,
    };
  });
};
